import React from 'react';
import { Typography } from '@material-ui/core';
import { styled } from '@material-ui/core/styles';

export interface FeatureProps {
  className?: string;
}

const Component: React.FC<FeatureProps> = ({ className }) => {
  const imgSrc1 = `${process.env.PUBLIC_URL}/images/workflow-img-feature-1.png`;
  const imgAlt1 = 'Feature1';
  const imgSrc2 = `${process.env.PUBLIC_URL}/images/workflow-img-feature-2.png`;
  const imgAlt2 = 'Feature2';
  const imgSrc3 = `${process.env.PUBLIC_URL}/images/workflow-img-feature-3.png`;
  const imgAlt3 = 'Feature3';

  interface FeatureBoxProps {
    imgSrc: string;
    imgAlt: string;
    imgOnLeft?: boolean;
    className?: string;
    children: React.ReactNode;
  }

  const FeatureBox: React.FC<FeatureBoxProps> = ({
    imgSrc,
    imgAlt,
    imgOnLeft = false,
    className,
    children,
  }) => {
    const RootBox = styled('article')(({ theme }) => ({
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      margin: theme.spacing(0, 'auto', 10),
      [theme.breakpoints.up(1281)]: {
        maxWidth: '1206px',
        flexDirection: imgOnLeft ? 'row-reverse' : 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
      },
      '&:last-of-type': {
        marginBottom: 0,
      },
    }));

    const ContentBox = styled('div')(({ theme }) => ({
      width: '100%',
      maxWidth: '446px',
      padding: theme.spacing(0),
      [theme.breakpoints.up(1281)]: {
        paddingTop: theme.spacing(8),
      },
    }));

    const ImgBox = styled('div')(({ theme }) => ({
      width: '100%',
      maxWidth: '680px',
      maxHeight: '384px',
      background:
        'transparent linear-gradient(119deg, #FCFEFF 0%, #DADFE6 100%) 0% 0% no-repeat padding-box',
      boxShadow: '0px 16px 32px #00000014',
      marginTop: theme.spacing(5),
      zIndex: 1,
      '& > img': {
        width: '100%',
      },
      [theme.breakpoints.up(1281)]: {
        marginTop: 0,
      },
    }));

    return (
      <RootBox className={className}>
        <ContentBox>{children}</ContentBox>
        <ImgBox>
          <img src={imgSrc} alt={imgAlt} />
        </ImgBox>
      </RootBox>
    );
  };

  const RootContainer = styled('section')(({ theme }) => ({
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    [theme.breakpoints.up(1281)]: {
      paddingRight: theme.spacing(8),
      paddingLeft: theme.spacing(8),
    },
  }));

  const HeadingComponent = styled('h2')(({ theme }) => ({
    marginBottom: theme.spacing(2),
    fontSize: '26px',
    fontWeight: 'bold',
    lineHeight: '39px',
    [theme.breakpoints.up(768)]: {
      fontSize: '32px',
      lineHeight: '48px',
    },
    '& span': { display: 'block' },
  }));

  const DescriptionComponent = styled('p')(({ theme }) => ({
    fontSize: '16px',
    lineHeight: '24px',
    '& strong': { fontWeight: 'bold' },
    '& span': { display: 'block' },
  }));

  return (
    <RootContainer className={className}>
      <FeatureBox imgSrc={imgSrc1} imgAlt={imgAlt1}>
        <Typography component={HeadingComponent}>
          かんたんペーパーレス化
        </Typography>
        <Typography component={DescriptionComponent}>
          <span>
            Excel®の申請フォームを取り込み、<strong>すぐに利用を開始</strong>
            することができます。
          </span>
          <span>
            申請フォームは<strong>スプレッドシート形式</strong>
            で作成できるため、<strong>使い慣れた操作感</strong>で
            <strong>直感的</strong>に設定することができます。
          </span>
          <span>
            押印機能など、紙の申請書をそのまま利用することを想定した機能を備えているため、紙の申請書からフォーマットを変更する必要なく
            <strong>最速でペーパーレス化を実現</strong>することができます。
          </span>
        </Typography>
      </FeatureBox>

      <FeatureBox imgSrc={imgSrc2} imgAlt={imgAlt2} imgOnLeft>
        <Typography component={HeadingComponent}>
          かんたん設定で日本企業の複雑な業務に対応
        </Typography>
        <Typography component={DescriptionComponent}>
          <span>
            ドラッグ＆ドロップで紙に描くようにフローを作成し、複雑なワークフローを
            <strong>ノーコード</strong>で<strong>簡単</strong>
            に設定することができます。
            <strong>専門的なスキルがなくても</strong>
            導入・運用することが可能です。
          </span>
          <span>
            申請者の組織に応じた承認経路の自動判定、申請書の内容に応じた条件分岐、代理申請・代理承認、印影など、
            <strong>日本企業の複雑な業務に対応</strong>する機能を
            <strong>簡単に設定</strong>することができます。
          </span>
        </Typography>
      </FeatureBox>

      <FeatureBox imgSrc={imgSrc3} imgAlt={imgAlt3}>
        <Typography component={HeadingComponent}>
          いつでも、どこでも、申請・承認
        </Typography>
        <Typography component={DescriptionComponent}>
          <span>
            モバイル・タブレット・ノートPCなど、デバイスを問わず、
            <strong>いつでもどこでも</strong>
            申請、承認、承認状況の確認を行うことができます。PCを利用できる状況でない場合や、移動中の場合でも承認業務は滞りません。
          </span>
        </Typography>
      </FeatureBox>
    </RootContainer>
  );
};

export const Feature = React.memo(Component);
